import { Archive as ArchiveIcon, PlusIcon, Sparkles as SparklesIcon } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';


import { OwnerBadge } from '../components/GehoertZu.js';
import { PageHeader } from '../components/PageHeader.js';
import { RdfExport } from '../components/RdfExport.js';
import { EmptyPanel, ErrorPanel, LoadingPanel } from '../components/StatePanel.js';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog.js';
import { Badge } from '../components/ui/badge.js';
import { Button } from '../components/ui/button.js';
import { Input } from '../components/ui/input.js';
import { Label } from '../components/ui/label.js';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select.js';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table.js';
import {
  api,
  ApiError,
  HVD_CATEGORY_LABELS,
  type CatalogSearchResult,
  type DatasetListResponse,
  type DatasetSummary,
  type HvdCategory,
} from '../lib/api.js';
import { formatCount } from '../lib/format.js';
import { canWriteAt, useOrganisations } from '../lib/organisations.js';
import { useWorkspace } from '../lib/workspace.js';

const siloLabels: Record<DatasetSummary['silo'], string> = {
  timescale: 'Zeitreihe',
  postgis: 'Geo',
  relational: 'Tabellarisch',
  citydb: '3D-Stadtmodell',
  object: 'Datei',
  external: 'Extern',
};

const SILO_VALUES = ['timescale', 'postgis', 'relational', 'citydb', 'object'] as const;
type SiloFilter = (typeof SILO_VALUES)[number];

function isSiloFilter(v: string | null): v is SiloFilter {
  return v !== null && (SILO_VALUES as readonly string[]).includes(v);
}

export function CatalogListPage(): JSX.Element {
  // Silos-Page verlinkt mit ?silo=<key> auf den Katalog — wir uebernehmen den
  // Filter unmittelbar. Suche und HVD-Filter bleiben lokal (kein URL-Sync,
  // bewusst klein gehalten).
  const [searchParams] = useSearchParams();
  const siloParam = searchParams.get('silo');
  const siloFilter = isSiloFilter(siloParam) ? siloParam : null;

  const [search, setSearch] = useState('');
  const [semantic, setSemantic] = useState<CatalogSearchResult | null>(null);
  // 'all' = kein Filter; sonst eine konkrete HVD-Kategorie. Der API-Endpoint
  // unterstuetzt den Filter (Catalog-Routen seit MR-HVD), wir geben ihn
  // direkt mit; die Liste reduziert sich serverseitig.
  const [hvdFilter, setHvdFilter] = useState<HvdCategory | 'all'>('all');
  const [data, setData] = useState<DatasetListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [archiveCandidate, setArchiveCandidate] = useState<DatasetSummary | null>(null);
  const [archiveBusy, setArchiveBusy] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  // Gliederung: Arbeitsbereich mit allem darunter; die ganze Gliederung laesst sich zuschalten.
  const ws = useWorkspace();
  const orgs = useOrganisations();
  const [whole, setWhole] = useState(false);
  const wsFilter = ws && !whole ? ws.node.id : undefined;

  async function performArchive(): Promise<void> {
    if (!archiveCandidate) return;
    setArchiveBusy(true);
    try {
      await api.archiveDataset(archiveCandidate.id);
      toast.success(`"${archiveCandidate.title}" archiviert.`);
      setArchiveCandidate(null);
      setReloadKey((k) => k + 1);
    } catch (err: unknown) {
      const msg =
        err instanceof ApiError ? `${err.code ?? 'error'}: ${err.message}` : String(err);
      toast.error(`Archivieren fehlgeschlagen: ${msg}`);
    } finally {
      setArchiveBusy(false);
    }
  }

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    const t = setTimeout(() => {
      api
        .listDatasets({
          search: search.trim() || undefined,
          hvd_category: hvdFilter === 'all' ? undefined : hvdFilter,
          silo: siloFilter ?? undefined,
          organisation: wsFilter,
          limit: 50,
        })
        .then((res) => {
          if (!cancelled) {
            setData(res);
            setLoading(false);
          }
        })
        .catch((err: unknown) => {
          if (cancelled) return;
          const msg =
            err instanceof ApiError ? `${err.code ?? 'error'}: ${err.message}` : String(err);
          setError(msg);
          setLoading(false);
        });
    }, search ? 250 : 0); // kleines Debounce auf der Suche

    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [search, hvdFilter, siloFilter, reloadKey, wsFilter]);

  // Semantische/hybride Suche (OpenSearch BM25 + Vektor) — additiv über der
  // ILIKE-Browse-Tabelle. Fällt still aus, wenn OpenSearch nicht verfügbar ist.
  useEffect(() => {
    const q = search.trim();
    if (q.length < 2) {
      setSemantic(null);
      return;
    }
    let cancelled = false;
    const t = setTimeout(() => {
      api
        .searchCatalog({ q, limit: 8 })
        .then((r) => {
          if (!cancelled) setSemantic(r);
        })
        .catch(() => {
          if (!cancelled) setSemantic(null);
        });
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [search]);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Datensätze"
        lead="DCAT-AP.de-konformes Verzeichnis aller registrierten Datensätze. Daten liegen in den Silos (TimescaleDB, PostGIS, Objektspeicher); der Katalog beschreibt sie und verweist auf die Eingangsquellen."
        actions={
          <div className="flex flex-wrap items-end gap-3">
            {ws ? (
              <label className="flex h-9 items-center gap-2 self-end text-sm">
                <input type="checkbox" checked={whole} onChange={(e) => setWhole(e.target.checked)} />
                Ganze Gliederung
              </label>
            ) : null}
            <div className="self-end">
              <RdfExport />
            </div>
            <div className="w-64 space-y-1.5">
              <Label htmlFor="catalog-search" className="sr-only">
                Suche (Titel, Slug, Beschreibung)
              </Label>
              <Input
                id="catalog-search"
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Suche Titel, Slug…"
              />
            </div>
            <div className="w-56 space-y-1.5">
              <Label htmlFor="catalog-hvd" className="sr-only">
                HVD-Kategorie
              </Label>
              <Select
                value={hvdFilter}
                onValueChange={(v) => setHvdFilter(v as HvdCategory | 'all')}
              >
                <SelectTrigger id="catalog-hvd">
                  <SelectValue placeholder="HVD-Kategorie" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle Datensätze</SelectItem>
                  {(Object.keys(HVD_CATEGORY_LABELS) as HvdCategory[]).map((k) => (
                    <SelectItem key={k} value={k}>
                      HVD · {HVD_CATEGORY_LABELS[k]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button asChild>
              <Link to="/sources/new">
                <PlusIcon className="mr-1 h-4 w-4" />
                Neue Quelle anbinden
              </Link>
            </Button>
          </div>
        }
      />

      {semantic && semantic.items.length > 0 ? (
        <div className="overflow-hidden rounded-xl border bg-card">
          <div className="flex items-center gap-2 border-b bg-muted/40 px-4 py-2 text-xs">
            <SparklesIcon className="h-3.5 w-3.5 text-primary" />
            <span className="font-medium">Semantische Treffer</span>
            <Badge variant="secondary" className="text-[10px]">
              OpenSearch · {semantic.mode === 'hybrid' ? 'hybrid (BM25 + Vektor)' : semantic.mode}
            </Badge>
            <span className="text-muted-foreground">{semantic.total} Treffer</span>
          </div>
          <ul className="divide-y">
            {semantic.items.map((it, i) => (
              <li key={it.id}>
                <Link
                  to={`/catalog/${it.id}`}
                  className="flex items-start gap-3 px-4 py-2.5 hover:bg-muted/50"
                >
                  <span className="mt-0.5 w-5 shrink-0 text-right font-mono text-xs text-muted-foreground">
                    {i + 1}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium">{it.title || it.slug}</span>
                    {it.description ? (
                      <span className="block truncate text-xs text-muted-foreground">
                        {it.description}
                      </span>
                    ) : null}
                  </span>
                  {it.themes?.length ? (
                    <span className="hidden shrink-0 gap-1 sm:flex">
                      {it.themes.slice(0, 2).map((t) => (
                        <Badge key={t} variant="secondary" className="text-[10px] font-normal">
                          {t}
                        </Badge>
                      ))}
                    </span>
                  ) : null}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {loading ? <LoadingPanel label="Katalog wird geladen..." /> : null}
      {error ? <ErrorPanel title="Konnte Katalog nicht laden" message={error} /> : null}

      {!loading && !error && data ? (
        data.items.length === 0 ? (
          <EmptyPanel illustration="catalog">Noch keine Datensätze im Katalog.</EmptyPanel>
        ) : (
          <div className="overflow-hidden rounded-xl border bg-card">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50 hover:bg-muted/50">
                  <TableHead className="px-4 text-xs uppercase tracking-wide">
                    Titel / Slug
                  </TableHead>
                  <TableHead className="text-xs uppercase tracking-wide">Silo</TableHead>
                  <TableHead className="text-xs uppercase tracking-wide">Owner</TableHead>
                  <TableHead className="text-xs uppercase tracking-wide">Tags</TableHead>
                  <TableHead className="text-xs uppercase tracking-wide text-right">
                    Datensätze
                  </TableHead>
                  <TableHead className="text-xs uppercase tracking-wide">HVD</TableHead>
                  <TableHead className="text-xs uppercase tracking-wide">Status</TableHead>
                  <TableHead className="text-xs uppercase tracking-wide">
                    Letzte Änderung
                  </TableHead>
                  <TableHead className="text-xs uppercase tracking-wide text-right">
                    Aktionen
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.items.map((d) => (
                  <TableRow key={d.id} className="cursor-pointer">
                    <TableCell className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Link
                          to={`/catalog/${d.id}`}
                          className="font-medium text-foreground hover:underline"
                        >
                          {d.title}
                        </Link>
                        {ws ? <OwnerBadge items={orgs.items} id={d.organisation_id} /> : null}
                        {d.harvest_source_id ? (
                          <Badge
                            className="border-transparent bg-amber-100 text-[10px] text-amber-900 dark:bg-amber-900/40 dark:text-amber-100"
                            title="Geharvetet aus einem fremden Katalog — Daten liegen extern, nicht in einem Silo."
                          >
                            geharvetet
                          </Badge>
                        ) : null}
                      </div>
                      <div className="text-xs text-muted-foreground">{d.slug}</div>
                    </TableCell>
                    <TableCell className="py-3">{siloLabels[d.silo]}</TableCell>
                    <TableCell className="py-3">{d.owner}</TableCell>
                    <TableCell className="py-3">
                      <div className="flex flex-wrap gap-1">
                        {d.tags.map((t) => (
                          <Badge key={t} variant="secondary" className="text-[10px] font-normal">
                            {t}
                          </Badge>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell className="py-3 text-right">
                      <RowCountCell value={d.row_count ?? null} />
                    </TableCell>
                    <TableCell className="py-3">
                      {d.hvd_category ? (
                        <Badge
                          variant="default"
                          className="text-[10px]"
                          title="High Value Dataset gem. EU-Verordnung 2023/138"
                        >
                          {HVD_CATEGORY_LABELS[d.hvd_category]}
                        </Badge>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell className="py-3">
                      <LifecycleBadge value={d.lifecycle} />
                    </TableCell>
                    <TableCell className="py-3 text-xs text-muted-foreground">
                      {new Date(d.updated_at).toLocaleString('de-DE')}
                    </TableCell>
                    <TableCell className="py-3 text-right">
                      {d.lifecycle === 'active' && canWriteAt(orgs, d.organisation_id) ? (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-muted-foreground hover:text-destructive"
                          title="Datensatz archivieren (Soft-Delete)"
                          aria-label={`Datensatz "${d.title}" archivieren`}
                          onClick={(e) => {
                            e.stopPropagation();
                            setArchiveCandidate(d);
                          }}
                        >
                          <ArchiveIcon className="h-4 w-4" />
                        </Button>
                      ) : null}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <div className="border-t px-4 py-2 text-xs text-muted-foreground">
              {data.total} {data.total === 1 ? 'Eintrag' : 'Einträge'}
            </div>
          </div>
        )
      ) : null}

      <AlertDialog
        open={archiveCandidate !== null}
        onOpenChange={(open) => {
          if (!open && !archiveBusy) setArchiveCandidate(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Datensatz archivieren?</AlertDialogTitle>
            <AlertDialogDescription>
              "{archiveCandidate?.title}" wird auf <code>lifecycle=archived</code> gesetzt
              und verschwindet aus dem Standard-Listing. Die Silo-Tabelle bleibt erhalten
              und kann separat geleert werden. Lineage, Audit-Log und Forecasts behalten
              ihre Verweise auf den Datensatz.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={archiveBusy}>Abbrechen</AlertDialogCancel>
            <AlertDialogAction onClick={performArchive} disabled={archiveBusy}>
              {archiveBusy ? 'Archiviere…' : 'Archivieren'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

/** Schildchen „Gehört zu“ (nur mit Gliederung). */
function RowCountCell({ value }: { value: number | null }): JSX.Element {
  if (value === null || value === undefined) {
    return <span className="text-xs text-muted-foreground">—</span>;
  }
  return (
    <span
      className="font-mono text-xs tabular-nums text-foreground"
      title="Aktueller Zeilenstand im Silo (geschätzt aus pg_class.reltuples, Cache 60s)"
    >
      {formatCount(value)}
    </span>
  );
}

function LifecycleBadge({ value }: { value: DatasetSummary['lifecycle'] }): JSX.Element {
  const labels: Record<DatasetSummary['lifecycle'], string> = {
    active: 'aktiv',
    deprecated: 'veraltet',
    archived: 'archiviert',
  };
  const variant: Record<DatasetSummary['lifecycle'], 'default' | 'secondary' | 'outline'> = {
    active: 'default',
    deprecated: 'secondary',
    archived: 'outline',
  };
  return (
    <Badge variant={variant[value]} className="text-[10px]">
      {labels[value]}
    </Badge>
  );
}
